"use client"

import React from 'react'
import {
    FormField,
} from '../ui/form'
import {
    Input,
    InputProps,
} from '../ui/new-input'
import {
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    Control,
} from 'react-hook-form'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Eye, EyeOff } from 'lucide-react'
import FormTextInputWrapper, { InputGroupProps } from './form-input-wrapper'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormTextInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormTextInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = InputProps & {
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps,
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>
}

/**
 * A form text input component that integrates with React Hook Form
 * 
 * This component renders a text input with form integration and optional password toggle.
 * It supports various input types including text, number, date, and password.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.variant - Input variant styling
 * @param props.size - Input size
 * @param props.inputProps - Additional input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormTextInput
 *   name="email"
 *   control={form.control}
 *   type="email"
 *   placeholder="Enter your email"
 *   inputGroupProps={{
 *     label: "Email Address",
 *   }}
 * />
 * ```
 * 
 * @throws {Error} When the name prop is not provided
 * 
 * @returns JSX.Element - The rendered text input component
 */
function FormTextInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    inputGroupProps,
    name,
    control,
    variant,
    size,
    ...inputProps
}: FormTextInputProps<TFieldValues, TName>) {
    // Validate that name is provided as it's required for form integration
    if (!name) {
        throw new Error('name is required')
    }

    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    // Check if this is a password field for toggle functionality
    const isPasswordField = inputProps.type === "password";
    const [showPassword, setShowPassword] = React.useState(false);

    // Determine the effective input type based on password toggle state
    const effectiveType = isPasswordField
        ? (showPassword ? "text" : "password")
        : inputProps.type

    /**
     * Renders the right icon based on input type
     * For password fields, shows toggle button; otherwise shows custom right icon
     */
    const renderRightIcon = () => {
        if (isPasswordField) {
            return <PasswordToggle showPassword={showPassword} setShowPassword={setShowPassword} />
        }
        return inputGroupProps?.rightIcon
    }

    /**
     * Handles input value changes with type-specific processing
     * 
     * @param e - Input change event
     * @param field - Form field render props
     */
    const onChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: ControllerRenderProps<TFieldValues, TName>
    ) => {
        const value = e.target.value
        if (inputProps.type === "number") {
            // Convert string to number for number inputs
            field.onChange(Number(value))
        } else if (inputProps.type === "date") {
            // Keep as string for date inputs
            field.onChange(value)
        } else {
            // Keep as string for text inputs
            field.onChange(value)
        }
    }

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                const errorMessage = fieldState.error?.message
                const isEmpty = field.value == null || field.value === ""
                const showEmptyHighlight = inputGroupProps?.highlightWhenEmpty && isEmpty
                return (
                    <FormTextInputWrapper
                        inputGroupProps={{
                            ...inputGroupProps,
                            rightIcon: renderRightIcon(),
                            isDirty: isDirty,
                        }}
                    >
                        <Input
                            {...inputProps}
                            {...field}
                            onChange={(e) => onChange(e, field)}
                            value={field.value || ""}
                            variant={!errorMessage ? variant : "destructive"}
                            size={size}
                            disabled={isReadOnly || inputProps.disabled}
                            className={cn(
                                "transition-colors duration-200",
                                isDirty && "border-[#9c27b0] focus:border-[#9c27b0] focus:ring-[#9c27b0]/20 shadow-sm",
                                showEmptyHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20",
                                inputProps.className
                            )}
                            style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : inputProps.style}
                            type={effectiveType}
                        />
                    </FormTextInputWrapper>
                )
            }}
        />
    )
}

/**
 * Password toggle button component
 * 
 * @param props - Component props
 * @param props.showPassword - Current password visibility state
 * @param props.setShowPassword - Function to toggle password visibility
 * 
 * @returns JSX.Element - The password toggle button
 */
const PasswordToggle = ({ showPassword, setShowPassword }: { showPassword: boolean, setShowPassword: (show: boolean) => void }) => (
    <Button
        variant={"ghost"}
        size={"icon"}
        type="button"
        onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowPassword(!showPassword)
        }}
        className='hover:bg-transparent hover:text-foreground cursor-pointer z-20 select-none pointer-events-auto'
        asChild
        showLoader={false}
    >
        {
            showPassword ?
                <EyeOff className="select-none pointer-events-none" /> :
                <Eye className="select-none pointer-events-none" />
        }
    </Button>
)

export default FormTextInput